import { APITypesV1 as APITypes } from "@cur8/api-client";
import { describeApiError } from "lib/error/problemDetails";
import { useCallback, useEffect, useState } from "react";
import { useAPIClient } from "render/context/APIContext";

// How often to ask again while the page is open, so it closes soon after the assessment
// moves on
const RECHECK_INTERVAL_MS = 60 * 1000;

export type AssessmentCheck =
  | { status: "checking" }
  | { status: "open" }
  | { status: "none" }
  | { status: "failed"; message: string };

/**
 * Whether the member has a derma assessment open for images, which is when DermaUI may
 * open them (AB#121888).
 *
 * A visit's derma assessment is in Draft from when its skin scan is processed until the
 * visit completes, or for a day at most, and is moved back to Draft for an hour when
 * AdminUI reopens it for a correction. After the visit it is New until the dermatologist
 * assesses it, which can take weeks. For now both count, the same states the backend
 * accepts images in. Draft alone would close the member when their visit ends.
 *
 * Asks again every minute and when the page comes back. A recheck that fails keeps the
 * last answer, so a moment offline does not close the page; the session timeout covers a
 * phone that stays offline.
 */
export function useOpenDermaAssessment(patientId: string, enabled: boolean) {
  const api = useAPIClient().assessment;
  const [check, setCheck] = useState<AssessmentCheck>({ status: "checking" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let current = true;

    function ask(isRecheck: boolean) {
      api
        .queryAssessments({
          patientId,
          assessmentTypes: ["DermaAssessmentItem"],
          assessmentStates: [
            APITypes.AssessmentState.Draft,
            APITypes.AssessmentState.New,
          ],
          isLatestVersion: true,
        })
        .result.then((response) => {
          if (!current) {
            return;
          }
          const status = response.items.length > 0 ? "open" : "none";
          // The same answer keeps the same state, so a recheck does not re-render the page
          setCheck((previous) =>
            previous.status === status ? previous : { status }
          );
        })
        .catch(async (error) => {
          console.error(
            "Could not check the member's derma assessment:",
            error
          );
          if (isRecheck) {
            return;
          }
          const message = await describeApiError(
            "Could not check the member's derma assessment",
            error
          );
          if (current) {
            setCheck({ status: "failed", message });
          }
        });
    }

    setCheck({ status: "checking" });
    ask(false);

    function recheck() {
      if (document.visibilityState === "visible") {
        ask(true);
      }
    }

    const interval = setInterval(recheck, RECHECK_INTERVAL_MS);
    // Ask the moment the phone wakes or the tab comes back
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("pageshow", recheck);
    window.addEventListener("focus", recheck);

    return () => {
      current = false;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("pageshow", recheck);
      window.removeEventListener("focus", recheck);
    };
  }, [api, patientId, enabled, attempt]);

  const retry = useCallback(() => {
    setAttempt((count) => count + 1);
  }, []);

  return { check, retry };
}
